import { FETCH_ALL_PARAMS } from 'constants/query';
import { useGetPlacesQuery } from 'features/place/services';
import { useGetWebsitesQuery } from 'features/website/services';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from 'app/hooks';
import { useCallback, useEffect, useState } from 'react';
import { ServerErrorType } from 'types/server-responses';
import { setErrorMessage } from 'store/toast-slice';
import { useAuth } from 'hooks/useAuth';
import { setWorkspace } from 'store/workspace-slice';
import { useRole } from 'hooks/useRole';

const useController = () => {
    const { companyId } = useAuth();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { data: websites, isLoading: isGetWebsitesLoading } = useGetWebsitesQuery(FETCH_ALL_PARAMS);
    const { data: places, isLoading: isGetPlacesLoading } = useGetPlacesQuery(FETCH_ALL_PARAMS);
    const [websiteId, setWebsiteId] = useState('');
    const [placeId, setPlaceId] = useState('');
    const { isAdmin } = useRole();

    useEffect(() => {
        if (!isGetWebsitesLoading && websites?.content?.[0]?.id) {
            setWebsiteId(websites.content[0].id);
        }
    }, [isGetWebsitesLoading, websites]);

    useEffect(() => {
        if (!isGetPlacesLoading && places?.content && websiteId) {
            const filteredPlaces = places.content
                .filter((place) => place.websiteId === websiteId)
                .sort((a, b) => a.name.localeCompare(b.name));

            setPlaceId(filteredPlaces[0]?.id || '');
        }
    }, [isGetPlacesLoading, places, websiteId]);

    if (websiteId && websites?.content.length === 1) {
        dispatch(
            setWorkspace({
                singleWebsite: true,
            }),
        );
    }

    const onChange = useCallback((value: string) => {
        setWebsiteId(value);
    }, []);

    const handleErrors = useCallback(
        (error: ServerErrorType) => {
            const code = error.data?.errorCode;
            dispatch(setErrorMessage({ key: code }));
        },
        [dispatch],
    );

    const validWebsite = websites?.content.some((website) => website.id === websiteId);

    const onConfirm = useCallback(
        (handleClose: () => void) => {
            if (validWebsite) {
                const websiteName = websites?.content.find((website) => website.id === websiteId)?.name || '';
                const baseWorkspaceUrl = isAdmin ? `/workspace/${websiteId}` : `/workspace/${websiteId}`;
                dispatch(
                    setWorkspace({
                        websiteId,
                        placeId,
                        websiteName,
                        openModal: false,
                        baseWorkspaceUrl,
                        companyId: companyId,
                    }),
                );
                navigate(baseWorkspaceUrl);
            } else {
                navigate('/not-found', { replace: true });
            }
            handleClose();
        },
        [navigate, websiteId, placeId, validWebsite, dispatch, websites?.content, companyId, isAdmin],
    );

    const onCancel = useCallback(() => {
        navigate('/');
    }, [navigate]);

    return {
        websites,
        websiteId,
        onConfirm,
        onCancel,
        onChange,
        isLoading: isGetWebsitesLoading,
        handleErrors,
    };
};

export default useController;
